import { ComparePageObjectModel } from '@/poms/comparePageObjectModel';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { ButtonBase, styled } from '@mui/material';
import { Fragment, ReactNode, useMemo, useState } from 'react';
import { collapseUnchangedLines, expandCollapsedEntries } from './collapseUnchangedLines';

const CollapsedLinesBar = styled(ButtonBase)(({ theme }) => ({
  gridColumn: '1 / -1',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  fontFamily: 'monospace',
  fontSize: 13,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.action.hover,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover': { backgroundColor: theme.palette.action.selected },
}));

export const CollapsibleLines = <T,>({
  lines,
  isUnchanged,
  getKey,
  renderLine,
}: {
  lines: T[];
  isUnchanged: (line: T) => boolean;
  getKey: (line: T) => string;
  renderLine: (line: T) => ReactNode;
}) => {
  const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const collapsedEntries = useMemo(() => collapseUnchangedLines(lines, isUnchanged), [lines, isUnchanged]);
  const entries = useMemo(
    () => expandCollapsedEntries(collapsedEntries, expandedKeys),
    [collapsedEntries, expandedKeys]
  );

  const expand = (key: string) => {
    setExpandedKeys(previous => new Set(previous).add(key));
  };

  return (
    <>
      {entries.map(entry =>
        entry.type === 'collapsed' ? (
          <CollapsedLinesBar
            key={entry.key}
            onClick={() => expand(entry.key)}
            data-testid={ComparePageObjectModel.TextDiffCollapsedLines}
          >
            <UnfoldMoreIcon fontSize='small' />
            {entry.lines.length} unchanged lines
          </CollapsedLinesBar>
        ) : (
          <Fragment key={getKey(entry.line)}>{renderLine(entry.line)}</Fragment>
        )
      )}
    </>
  );
};
